import { useToast } from "@/hooks/use-toast";
import { uploadApplicationDocument } from "@/services/storageService";
import { usePersonalDetailsCloudFunctions } from "./usePersonalDetailsCloudFunctions";
import { generateContractPdf, blobToFile } from "@/utils/pdfUtils";
import { useNavigate } from "react-router-dom";
import { useApplication } from "@/contexts/ApplicationContext";
import { getVRFContractText } from "@/utils/contractUtils";
import * as Sentry from "@sentry/react";

interface UsePersonalDetailsSubmissionHandlersProps {
  // Identity props
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex: string;

  // Bank details props
  bankName: string;
  accountNumber: string;
  sortCode: string;
  accountName: string;
  bankProofFile: File | null;
  bankProofPreview: string | null;

  // Next of kin props
  nextOfKinName: string;
  nextOfKinPhone: string;
  nextOfKinRelation: string;

  // Tax details props
  nationalInsuranceNumber: string;
  utrNumber?: string;
  companyType: string;
  companyNumber?: string;
  vatNumber?: string;

  // Address details props
  addressLine1: string;
  addressLine2?: string;
  addressCity: string;
  addressPostcode: string;

  // Headshot props
  headshotFile: File | null;
  headshotPreview: string | null;

  // Final forms props
  signatureData: string | null;

  // Navigation control
  setActiveTab: (tab: string) => void;
}

export const usePersonalDetailsSubmissionHandlers = ({
  // Identity props
  firstName,
  lastName,
  dateOfBirth,
  sex,

  // Bank details props
  bankName,
  accountNumber,
  sortCode,
  accountName,
  bankProofFile,

  // Next of kin props
  nextOfKinName,
  nextOfKinPhone,
  nextOfKinRelation,

  // Tax details props
  nationalInsuranceNumber,
  utrNumber,
  companyType,
  companyNumber,
  vatNumber,

  // Address details props
  addressLine1,
  addressLine2,
  addressCity,
  addressPostcode,

  // Headshot props
  headshotFile,

  // Final forms props
  signatureData,
  // Navigation control
  setActiveTab,
}: UsePersonalDetailsSubmissionHandlersProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { application, refetch } = useApplication();
  const {
    submitIdentity,
    submitBankDetails,
    submitNextOfKin,
    submitTaxDetails,
    submitAddressDetails,
    submitHeadshot,
    submitFinalForms,
  } = usePersonalDetailsCloudFunctions();

  // Centralized navigation/tab progression handler
  const handleNextNavigation = (result: unknown, fallback: () => void) => {
    // Helper to safely extract string props
    const tryGet = (
      obj: unknown,
      key: "nextStage" | "nextSegment"
    ): string | undefined => {
      if (obj && typeof obj === "object") {
        const val = (obj as Record<string, unknown>)[key];
        return typeof val === "string" ? val : undefined;
      }
      return undefined;
    };

    // Support both shapes: top-level and nested under `data`
    let nested: unknown;
    if (result && typeof result === "object" && "data" in result) {
      nested = (result as { data: unknown }).data;
    }

    const nextStage =
      tryGet(result, "nextStage") ?? tryGet(nested, "nextStage");
    if (nextStage) {
      navigate(`/${nextStage}`);
      return;
    }

    const nextSegment =
      tryGet(result, "nextSegment") ?? tryGet(nested, "nextSegment");
    if (nextSegment) {
      setActiveTab(nextSegment);
      return;
    }

    // Fallback to caller-provided behavior
    fallback();
  };

  const handleIdentitySubmit = async () => {
    try {
      const result = await submitIdentity.mutateAsync({
        applicationId: application.applicationId,
        firstName,
        lastName,
        dateOfBirth,
        sex,
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description:
            result.message || "Failed to submit identity confirmation",
          variant: "destructive",
        });
        return;
      }

      await refetch();
      toast({
        title: "Identity Confirmed",
        description: "Your identity details have been saved.",
      });
      handleNextNavigation(result, () => setActiveTab("bank-details"));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "PersonalDetailsForm.handleIdentitySubmit",
          applicationId: application?.id,
          firstName,
          lastName,
          dateOfBirth,
          sex,
        },
      });
      console.error("Error in identity submission:", error);
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleBankDetailsSubmit = async () => {
    try {
      const applicationId = application?.id;

      let bankProofUrl = null;

      // Upload bank proof if available
      if (bankProofFile) {
        const bankProofResult = await uploadApplicationDocument(
          bankProofFile,
          applicationId,
          "bank-proof"
        );

        if (!bankProofResult.success) {
          Sentry.captureException(bankProofResult);
          toast({
            title: "Upload Error",
            description:
              bankProofResult.message || "Failed to upload bank proof",
            variant: "destructive",
          });
          return;
        }

        bankProofUrl = bankProofResult.url;
      }

      // Submit to cloud function
      const result = await submitBankDetails.mutateAsync({
        applicationId: application.applicationId,
        bankName,
        accountNumber,
        sortCode,
        accountName,
        bankProofUrl,
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description: result.message || "Failed to submit bank details",
          variant: "destructive",
        });
        return;
      }

      await refetch();
      toast({
        title: "Bank Details Submitted",
        description: "Your bank details have been successfully submitted.",
      });
      handleNextNavigation(result, () => setActiveTab("next-of-kin"));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "PersonalDetailsForm.handleBankDetailsSubmit",
          applicationId: application?.id,
          bankName,
          accountNumber,
          sortCode,
          accountName,
          bankProofFile,
        },
      });
      console.error("Error in bank details submission:", error);
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleNextOfKinSubmit = async () => {
    try {
      // Submit to cloud function
      const result = await submitNextOfKin.mutateAsync({
        applicationId: application.applicationId,
        name: nextOfKinName,
        phone: nextOfKinPhone,
        relation: nextOfKinRelation,
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description: result.message || "Failed to submit next of kin details",
          variant: "destructive",
        });
        return;
      }

      await refetch();
      toast({
        title: "Next of Kin Details Submitted",
        description:
          "Your next of kin details have been successfully submitted.",
      });
      handleNextNavigation(result, () => setActiveTab("tax-details"));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "PersonalDetailsForm.handleNextOfKinSubmit",
          applicationId: application?.id,
          nextOfKinName,
          nextOfKinPhone,
          nextOfKinRelation,
        },
      });
      console.error("Error in next of kin submission:", error);
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleTaxDetailsSubmit = async () => {
    try {
      // Submit to cloud function
      const result = await submitTaxDetails.mutateAsync({
        applicationId: application.applicationId,
        nationalInsuranceNumber,
        utrNumber,
        companyType,
        companyNumber,
        vatNumber,
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description: result.message || "Failed to submit tax details",
          variant: "destructive",
        });
        return;
      }

      await refetch();
      toast({
        title: "Tax Details Submitted",
        description: "Your tax details have been successfully submitted.",
      });
      handleNextNavigation(result, () => setActiveTab("address-details"));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "PersonalDetailsForm.handleTaxDetailsSubmit",
          applicationId: application?.id,
          nationalInsuranceNumber,
          utrNumber,
          companyType,
          companyNumber,
          vatNumber,
        },
      });
      console.error("Error in tax details submission:", error);
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleAddressDetailsSubmit = async () => {
    try {
      // Submit to cloud function
      const result = await submitAddressDetails.mutateAsync({
        applicationId: application.applicationId,
        addressLine1,
        addressLine2,
        addressCity,
        addressPostcode,
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description: result.message || "Failed to submit address details",
          variant: "destructive",
        });
        return;
      }

      await refetch();
      toast({
        title: "Address Details Submitted",
        description: "Your address details have been successfully submitted.",
      });
      handleNextNavigation(result, () => setActiveTab("headshot"));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "PersonalDetailsForm.handleAddressDetailsSubmit",
          applicationId: application?.id,
          addressLine1,
          addressLine2,
          addressCity,
          addressPostcode,
        },
      });
      console.error("Error in address details submission:", error);
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleHeadshotSubmit = async () => {
    try {
      const applicationId = application?.id;

      let headshotUrl = null;

      // Upload headshot if available
      if (headshotFile) {
        const headshotResult = await uploadApplicationDocument(
          headshotFile,
          applicationId,
          "headshot"
        );

        if (!headshotResult.success) {
          toast({
            title: "Upload Error",
            description: headshotResult.message || "Failed to upload headshot",
            variant: "destructive",
          });
          return;
        }

        headshotUrl = headshotResult.url;
      }

      // Submit to cloud function
      const result = await submitHeadshot.mutateAsync({
        applicationId: application.applicationId,
        headshotUrl,
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description: result.message || "Failed to submit headshot",
          variant: "destructive",
        });
        return;
      }

      await refetch();
      toast({
        title: "Headshot Submitted",
        description: "Your headshot has been successfully submitted.",
      });
      handleNextNavigation(result, () => {
        if (application?.basicDetails?.needsVan) {
          setActiveTab("vehicle-release-form");
        } else {
          navigate("/documents");
        }
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "PersonalDetailsForm.handleHeadshotSubmit",
          applicationId: application?.id,
          headshotFile,
        },
      });
      console.error("Error in headshot submission:", error);
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleFinalFormsSubmit = async () => {
    try {
      const applicationId = application?.id;
      const applicantName =
        application?.basicDetails?.firstName +
          " " +
          application?.basicDetails?.lastName || "Applicant";

      if (!signatureData) {
        toast({
          title: "Signature Required",
          description: "Please sign the contract before submitting",
          variant: "destructive",
        });
        return;
      }

      // Step 1: Generate the combined PDF with contract text and signature
      const contractText = getVRFContractText();
      let contractPdfUrl = null;

      try {
        // Generate the PDF
        const pdfBlob = await generateContractPdf(
          contractText,
          signatureData,
          applicantName
        );

        // Convert blob to file for upload
        const timestamp = new Date().getTime();
        const pdfFile = blobToFile(
          pdfBlob,
          `contract-${application.applicationId}-${timestamp}.pdf`,
          "application/pdf"
        );

        // Upload the PDF to storage
        const contractPdfResult = await uploadApplicationDocument(
          pdfFile,
          applicationId,
          "contract-pdf"
        );

        if (!contractPdfResult.success) {
          Sentry.captureException(contractPdfResult);
          toast({
            title: "PDF Upload Error",
            description:
              contractPdfResult.message || "Failed to upload contract PDF",
            variant: "destructive",
          });
          return;
        }

        contractPdfUrl = contractPdfResult.url;
      } catch (pdfError) {
        console.error("Error generating or uploading PDF:", pdfError);
        toast({
          title: "PDF Generation Error",
          description:
            pdfError instanceof Error
              ? pdfError.message
              : "Failed to generate contract PDF",
          variant: "destructive",
        });
        return;
      }

      // // Submit to cloud function with both signature and contract PDF URLs
      const result = await submitFinalForms.mutateAsync({
        applicationId: application.applicationId,
        contractPdfUrl,
        personalDetailsCompleted: true,
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description: result.message || "Failed to submit final forms",
          variant: "destructive",
        });
        return;
      }

      await refetch();
      toast({
        title: "Final Forms Submitted",
        description:
          "Your contract has been signed and submitted successfully.",
      });

      handleNextNavigation(result, () => {
        // Default progression after final forms
        navigate("/documents");
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "PersonalDetailsForm.handleFinalFormsSubmit",
          applicationId: application?.id,
          personalDetailsCompleted: true,
        },
      });
      console.error("Error in final forms submission:", error);
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  return {
    handleIdentitySubmit,
    handleBankDetailsSubmit,
    handleNextOfKinSubmit,
    handleTaxDetailsSubmit,
    handleAddressDetailsSubmit,
    handleHeadshotSubmit,
    handleFinalFormsSubmit,
  };
};
